import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { cartItemKey, useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/apiClient';
import { theme } from '../styles/theme';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import SectionTitle from '../components/ui/SectionTitle';
import BackButton from '../components/ui/BackButton';
import { cartProblemLabel } from '../components/cart/CartItemNotice';
import { formatSqft } from '../utils/sizeEstimate';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_placeholder');

const CheckoutPage = () => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm />
    </Elements>
  );
};

const CheckoutForm = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const {
    cartItems, subtotal, clearCart, availability,
    unavailableItems, hasUnavailableItems, recheckAvailability,
  } = useCart();
  const { userProfile, isApproved, pricingTier } = useAuth();

  // Products can be turned off or sell out while they sit in a cart.
  useEffect(() => {
    recheckAvailability();
  }, [recheckAvailability]);
  
  // Check if user has net30 payment terms
  const isNet30User = userProfile?.net30 || false;
  
  // Wholesale/volume customers on standard shipping + save card flow get tax shown as TBD
  const isWholesaleOrVolume = pricingTier === 'wholesale' || pricingTier === 'volume';
  
  // Ref for error message to scroll to
  const errorRef = useRef(null);
  
  // Ref-based guard to prevent duplicate submissions (synchronous, unlike useState)
  const isSubmitting = useRef(false);
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [setupClientSecret, setSetupClientSecret] = useState('');
  const [paymentClientSecret, setPaymentClientSecret] = useState('');
  const [paymentConsent, setPaymentConsent] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState('');
  const [wasImmediatePayment, setWasImmediatePayment] = useState(false);
  const [customerNotes, setCustomerNotes] = useState('');
  
  // Address management
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [useNewAddress, setUseNewAddress] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    full_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: '',
  });
  
  // Note: useSameAddress is always true for now - billing address same as shipping
  const useSameAddress = true;
  
  // Payment method state (for net30 users)
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'invoice'
  
  // Shipping method state
  const [shippingMethod, setShippingMethod] = useState('standard'); // 'standard' or 'customer_pickup'
  const isPickup = shippingMethod === 'customer_pickup';

  // Pieces that vary in size (hides) are priced by estimate and re-priced from
  // their measured size before the card is charged — so the customer has to
  // agree to that, and even pickup orders save the card instead of paying now.
  const approxSqftOf = (item) => availability[cartItemKey(item)]?.approx_sqft;
  const hasSizeEstimates = cartItems.some((item) => approxSqftOf(item));
  const [sizeEstimateAccepted, setSizeEstimateAccepted] = useState(false);
  const chargesNow = isPickup && !hasSizeEstimates;

  // Totals
  const [shippingCost, setShippingCost] = useState(0);
  const [tax, setTax] = useState(0);
  const [total, setTotal] = useState(0);

  // Load saved addresses on mount
  useEffect(() => {
    const loadAddresses = async () => {
      try {
        const addresses = await api.addresses.list();
        setSavedAddresses(addresses);
        
        // Set default address if exists
        const defaultAddress = addresses.find(addr => addr.is_default);
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
          setShippingAddress({
            full_name: defaultAddress.full_name,
            address_line1: defaultAddress.address_line1,
            address_line2: defaultAddress.address_line2 || '',
            city: defaultAddress.city,
            state: defaultAddress.state || '',
            postal_code: defaultAddress.postal_code,
            country: defaultAddress.country,
            phone: defaultAddress.phone || '',
          });
        }
      } catch (err) {
        console.error('Error loading addresses:', err);
      }
    };
    
    if (isApproved) {
      loadAddresses();
    }
  }, [isApproved]);

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  }, [error]);

  // Calculate totals and shipping cost based on method
  useEffect(() => {
    // Calculate shipping cost based on method
    let calculatedShippingCost = 0;
    if (shippingMethod === 'customer_pickup') {
      calculatedShippingCost = 0; // No shipping for customer pickup
    } else {
      // Standard shipping calculation (could be updated based on backend logic)
      calculatedShippingCost = 0; // TBD by admin
    }
    setShippingCost(calculatedShippingCost);
    
    const calculatedTax = subtotal * 0.08; // 8% tax
    setTax(calculatedTax);
    setTotal(subtotal + calculatedShippingCost + calculatedTax);
  }, [subtotal, shippingMethod]);

  // Handle address selection change
  const handleAddressSelect = (addressId) => {
    setSelectedAddressId(addressId);
    setUseNewAddress(false);
    
    const selected = savedAddresses.find(addr => addr.id === addressId);
    if (selected) {
      setShippingAddress({
        full_name: selected.full_name,
        address_line1: selected.address_line1,
        address_line2: selected.address_line2 || '',
        city: selected.city,
        state: selected.state || '',
        postal_code: selected.postal_code,
        country: selected.country,
        phone: selected.phone || '',
      });
    }
  };

  // Handle new address
  const handleUseNewAddress = () => {
    setUseNewAddress(true);
    setSelectedAddressId('');
    setShippingAddress({
      full_name: userProfile?.full_name || '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
      phone: userProfile?.phone || '',
    });
  };

  // Handle form submission
  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    if (isSubmitting.current) return;
    if (hasUnavailableItems) {
      setError('Some items in your cart can’t be ordered right now. Update or remove them in your cart, then try again.');
      return;
    }
    isSubmitting.current = true;
    setError('');
    setLoading(true);

    try {
      // Validate address
      if (!shippingAddress.full_name || !shippingAddress.address_line1 || 
          !shippingAddress.city || !shippingAddress.postal_code) {
        setError('Please fill in all required address fields.');
        setLoading(false);
        isSubmitting.current = false;
        return;
      }

      if (hasSizeEstimates && !sizeEstimateAccepted) {
        setError('Some items vary in size. Please check the box confirming your final price will be based on their actual size.');
        setLoading(false);
        isSubmitting.current = false;
        return;
      }

      // For card payments, validate consent and card details
      const useCardPayment = !(isNet30User && paymentMethod === 'invoice');
      if (useCardPayment && !paymentConsent) {
        setError('Please consent to future charges by checking the box above.');
        setLoading(false);
        isSubmitting.current = false;
        return;
      }

      if (useCardPayment && !cardComplete) {
        setError('Please enter valid card details before submitting.');
        setLoading(false);
        isSubmitting.current = false;
        return;
      }

      // For card payments, validate Stripe is loaded
      if (useCardPayment && (!stripe || !elements)) {
        setError('Payment system is still loading. Please wait a moment and try again.');
        setLoading(false);
        isSubmitting.current = false;
        return;
      }

      // Reuse existing order on retry to prevent duplicates
      let currentOrderId = orderId;
      let currentSetupSecret = setupClientSecret;
      let currentPaymentSecret = paymentClientSecret;

      if (!currentOrderId) {
        const checkoutData = {
          items: cartItems.map(item => ({
            product_id: item.product_id || item.id,
            subcategory_id: item.subcategory || item.subcategory?.id || null,
            quantity: item.quantity,
            selected_components: item.selected_components || [],
          })),
          shipping_address: shippingAddress,
          billing_address: useSameAddress ? shippingAddress : shippingAddress,
          use_invoice: isNet30User && paymentMethod === 'invoice',
          shipping_method: shippingMethod,
          customer_notes: customerNotes.trim() || null,
          size_estimate_accepted: sizeEstimateAccepted,
        };

        const session = await api.checkout.createSession(checkoutData);

        currentOrderId = session.order_id;
        currentSetupSecret = session.setup_client_secret || '';
        currentPaymentSecret = session.client_secret || '';

        setOrderId(currentOrderId);
        setSetupClientSecret(currentSetupSecret);
        setPaymentClientSecret(currentPaymentSecret);

        // Invoice orders complete immediately — no Stripe step to fail/retry
        if (session.payment_provider === 'invoice') {
          setSuccess(true);
          clearCart();
          setLoading(false);
          isSubmitting.current = false;
          
          setTimeout(() => {
            navigate('/dashboard', { 
              state: { 
                message: 'Order submitted successfully! You will receive an invoice via email with net 30 payment terms.',
                orderId: currentOrderId 
              } 
            });
          }, 2000);
          return;
        }
      }

      // For customer pickup with immediate payment: PaymentIntent
      if (currentPaymentSecret && !currentSetupSecret) {
        const cardElement = elements.getElement(CardElement);
        
        if (!cardElement) {
          throw new Error('Card input not found. Please refresh and try again.');
        }

        const { paymentIntent, error: stripeError } = await stripe.confirmCardPayment(
          currentPaymentSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: shippingAddress.full_name,
                address: {
                  line1: shippingAddress.address_line1,
                  line2: shippingAddress.address_line2,
                  city: shippingAddress.city,
                  state: shippingAddress.state,
                  postal_code: shippingAddress.postal_code,
                  country: shippingAddress.country,
                },
              },
            },
          }
        );

        if (stripeError) {
          try { await api.checkout.markCheckoutIncomplete(currentOrderId); } catch (_) {}
          throw new Error(stripeError.message || 'Payment failed. Please try again.');
        }

        if (paymentIntent.status === 'succeeded') {
          await api.payments.confirmPayment(currentOrderId);
          
          setWasImmediatePayment(true);
          setSuccess(true);
          clearCart();
          setLoading(false);
          isSubmitting.current = false;
          
          setTimeout(() => {
            navigate('/dashboard', { 
              state: { 
                message: 'Payment successful! Your order has been placed and will be ready for pickup once processed.',
                orderId: currentOrderId 
              } 
            });
          }, 2000);
        } else {
          throw new Error(`Payment failed with status: ${paymentIntent.status}`);
        }
        return;
      }

      // For standard shipping: Setup card on file
      if (currentSetupSecret) {
        const cardElement = elements.getElement(CardElement);
        
        if (!cardElement) {
          throw new Error('Card input not found. Please refresh and try again.');
        }

        const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(
          currentSetupSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: shippingAddress.full_name,
                address: {
                  line1: shippingAddress.address_line1,
                  line2: shippingAddress.address_line2,
                  city: shippingAddress.city,
                  state: shippingAddress.state,
                  postal_code: shippingAddress.postal_code,
                  country: shippingAddress.country,
                },
              },
            },
          }
        );

        if (stripeError) {
          try { await api.checkout.markCheckoutIncomplete(currentOrderId); } catch (_) {}
          throw new Error(stripeError.message || 'Failed to save card. Please try again.');
        }

        if (setupIntent.status === 'succeeded') {
          await api.checkout.confirmCardSetup(currentOrderId);
          
          setSuccess(true);
          clearCart();
          setLoading(false);
          isSubmitting.current = false;
          
          setTimeout(() => {
            navigate('/dashboard', { 
              state: { 
                message: hasSizeEstimates
                  ? 'Card saved successfully! We’ll measure your pieces, confirm the final total, and then charge your card.'
                  : 'Card saved successfully! Your order will be reviewed and charged once shipping is calculated.',
                orderId: currentOrderId 
              } 
            });
          }, 2000);
        } else {
          throw new Error(`Card setup failed with status: ${setupIntent.status}`);
        }
      } else {
        throw new Error('Invalid checkout session response');
      }
    } catch (err) {
      console.error('Order error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to place order. Please try again.');
      setLoading(false);
      isSubmitting.current = false;
    }
  };

  // Styles
  const containerStyles = {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: `${theme.spacing.xxl} ${theme.spacing.lg}`,
    minHeight: '70vh',
  };

  const gridStyles = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: theme.spacing.xl,
  };

  const inputStyles = {
    width: '100%',
    padding: theme.spacing.md,
    border: `2px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.lg,
    fontSize: theme.typography.fontSize.base,
    fontFamily: theme.typography.fontFamily.secondary,
    transition: 'all 0.3s ease',
    backgroundColor: theme.colors.surface,
  };

  const labelStyles = {
    display: 'block',
    marginBottom: theme.spacing.sm,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.text,
    fontFamily: theme.typography.fontFamily.secondary,
  };

  const radioCardStyles = (isSelected) => ({
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.md,
    border: `2px solid ${isSelected ? theme.colors.primary : theme.colors.borderLight}`,
    borderRadius: theme.borderRadius.lg,
    cursor: 'pointer',
    backgroundColor: isSelected ? theme.colors.surfaceAlt : theme.colors.surface,
    transition: 'all 0.3s ease',
    boxShadow: isSelected ? theme.shadows.md : theme.shadows.sm,
  });

  // Show success message
  if (success) {
    return (
      <div style={{ 
        ...containerStyles, 
        textAlign: 'center', 
        paddingTop: theme.spacing.xxl,
        backgroundColor: theme.colors.background,
      }}>
        <Card 
          padding="xxl"
          style={{
            background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
            border: `1px solid ${theme.colors.borderLight}`,
            boxShadow: theme.shadows.xl,
          }}
        >
          <div style={{ 
            fontSize: '6rem', 
            marginBottom: theme.spacing.xl,
            animation: 'scaleIn 0.5s ease-out',
            color: theme.colors.success,
          }}>
            ✓
          </div>
          <h2 style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.typography.fontSize['4xl'],
            color: theme.colors.success,
            marginBottom: theme.spacing.lg,
            fontWeight: theme.typography.fontWeight.bold,
          }}>
            Order Submitted Successfully!
          </h2>
          <p style={{ 
            fontSize: theme.typography.fontSize.xl, 
            color: theme.colors.text,
            marginBottom: theme.spacing.md,
            fontWeight: theme.typography.fontWeight.semibold,
          }}>
            Order ID: <span style={{ color: theme.colors.primary }}>{orderId}</span>
          </p>
          <p style={{ 
            marginBottom: theme.spacing.xxl,
            fontSize: theme.typography.fontSize.lg,
            color: theme.colors.textSecondary,
            lineHeight: theme.typography.lineHeight.relaxed,
            maxWidth: '600px',
            margin: `0 auto ${theme.spacing.xxl}`,
          }}>
            {isNet30User && paymentMethod === 'invoice'
              ? 'Your order has been submitted for review. An admin will review your order and adjust the final price if needed. You will receive an invoice via email with net 30 payment terms.'
              : wasImmediatePayment
                ? 'Your payment has been processed successfully! A confirmation email has been sent to you. We\'ll prepare your order and notify you when it\'s ready for pickup.'
                : shippingMethod === 'customer_pickup'
                  ? 'Your card has been saved securely! Our team will review your order and adjust the final price if needed based on current inventory. Your card will then be charged automatically, and you\'ll receive a confirmation email.'
                  : 'Your card has been saved securely! Our team will calculate shipping costs and adjust the final price if needed. Your card will then be charged automatically, and you\'ll receive a confirmation email.'}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: `${theme.spacing.md} ${theme.spacing.xxl}`,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              fontFamily: theme.typography.fontFamily.secondary,
              background: `linear-gradient(135deg, ${theme.colors.cta} 0%, ${theme.colors.primaryLight} 100%)`,
              color: '#fff',
              border: 'none',
              borderRadius: theme.borderRadius.lg,
              boxShadow: '0 4px 12px rgba(204, 85, 0, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing.sm,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(204, 85, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(204, 85, 0, 0.3)';
            }}
          >
            View Order Details
          </button>
        </Card>
        <style>{`
          @keyframes scaleIn {
            from {
              transform: scale(0);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  }

  // Check if user is approved
  if (!isApproved) {
    return (
      <div style={{ 
        ...containerStyles, 
        textAlign: 'center',
        backgroundColor: theme.colors.background,
      }}>
        <Card 
          padding="xxl"
          style={{
            background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
            border: `1px solid ${theme.colors.borderLight}`,
            boxShadow: theme.shadows.xl,
          }}
        >
          <h3 style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.typography.fontSize['3xl'],
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text,
            marginBottom: theme.spacing.md,
          }}>
            Account Approval Required
          </h3>
          <p style={{ 
            fontSize: theme.typography.fontSize.lg, 
            color: theme.colors.textSecondary,
            marginBottom: theme.spacing.xxl,
          }}>
            Your account needs to be approved before you can checkout.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: `${theme.spacing.md} ${theme.spacing.xxl}`,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              fontFamily: theme.typography.fontFamily.secondary,
              background: `linear-gradient(135deg, ${theme.colors.cta} 0%, ${theme.colors.primaryLight} 100%)`,
              color: '#fff',
              border: 'none',
              borderRadius: theme.borderRadius.lg,
              boxShadow: '0 4px 12px rgba(204, 85, 0, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(204, 85, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(204, 85, 0, 0.3)';
            }}
          >
            Go to Dashboard
          </button>
        </Card>
      </div>
    );
  }

  // Check if cart is empty
  if (cartItems.length === 0) {
    return (
      <div style={{ 
        ...containerStyles, 
        textAlign: 'center',
        backgroundColor: theme.colors.background,
      }}>
        <Card 
          padding="xxl"
          style={{
            background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
            border: `1px solid ${theme.colors.borderLight}`,
            boxShadow: theme.shadows.xl,
          }}
        >
          <h3 style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.typography.fontSize['3xl'],
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text,
            marginBottom: theme.spacing.md,
          }}>
            Your cart is empty
          </h3>
          <p style={{ 
            fontSize: theme.typography.fontSize.lg, 
            color: theme.colors.textSecondary,
            marginBottom: theme.spacing.xxl,
          }}>
            Add some items to your cart before checking out.
          </p>
          <button
            onClick={() => navigate('/products')}
            style={{
              padding: `${theme.spacing.md} ${theme.spacing.xxl}`,
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.bold,
              fontFamily: theme.typography.fontFamily.secondary,
              background: `linear-gradient(135deg, ${theme.colors.cta} 0%, ${theme.colors.primaryLight} 100%)`,
              color: '#fff',
              border: 'none',
              borderRadius: theme.borderRadius.lg,
              boxShadow: '0 4px 12px rgba(204, 85, 0, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing.sm,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(204, 85, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(204, 85, 0, 0.3)';
            }}
          >
            <span>Start Shopping</span>
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: theme.colors.background, minHeight: '100vh' }}>
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid rgba(255,255,255,0.3)',
            borderTop: `4px solid ${theme.colors.primary}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: theme.spacing.lg,
          }} />
          <div style={{
            color: '#fff',
            fontSize: theme.typography.fontSize.xl,
            fontWeight: theme.typography.fontWeight.bold,
            fontFamily: theme.typography.fontFamily.primary,
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
          }}>
            Processing your order...
          </div>
          <div style={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: theme.typography.fontSize.base,
            fontFamily: theme.typography.fontFamily.secondary,
            marginTop: theme.spacing.sm,
          }}>
            Please do not close or refresh this page
          </div>
        </div>
      )}
      <div style={containerStyles}>
        <BackButton 
          to="/cart"
          label="Back to Cart"
          variant="ghost"
          style={{ marginBottom: theme.spacing.lg }}
        />
        
        <SectionTitle size="xl" withDivider>
          Checkout
        </SectionTitle>

        {hasUnavailableItems && (
          <Card
            padding="lg"
            role="alert"
            style={{
              marginBottom: theme.spacing.xl,
              border: '1px solid #FCA5A5',
              backgroundColor: '#FEF2F2',
              color: '#991B1B',
            }}
          >
            <p style={{ fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.sm }}>
              Some items in your cart can’t be ordered right now
            </p>
            <ul style={{ margin: `0 0 ${theme.spacing.md}`, paddingLeft: '1.25rem', lineHeight: 1.6 }}>
              {unavailableItems.map(({ key, item, status }) => {
                const variant = item.variantName || item.subcategory_name;
                return (
                  <li key={key}>
                    {item.name}{variant ? ` (${variant})` : ''}: {cartProblemLabel(status)}
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              onClick={() => navigate('/cart')}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                border: '1px solid #991B1B',
                borderRadius: theme.borderRadius.md,
                backgroundColor: '#fff',
                color: '#991B1B',
                fontWeight: theme.typography.fontWeight.semibold,
                cursor: 'pointer',
              }}
            >
              Review your cart
            </button>
          </Card>
        )}

        {error && (
          <div ref={errorRef}>
            <Card 
              padding="lg" 
              style={{ 
                background: `linear-gradient(135deg, ${theme.colors.error} 0%, #dc2626 100%)`,
                color: '#fff',
                marginBottom: theme.spacing.xl,
                border: 'none',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
              }}
            >
              <div style={{ 
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.medium,
                lineHeight: theme.typography.lineHeight.relaxed,
              }}>
                <div style={{ marginBottom: theme.spacing.sm }}>
                  {error}
                </div>
                <div style={{ 
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.normal,
                  opacity: 0.95,
                  marginTop: theme.spacing.md,
                  paddingTop: theme.spacing.md,
                  borderTop: '1px solid rgba(255, 255, 255, 0.3)',
                }}>
                  If issues persist, please contact us at <strong style={{ fontWeight: theme.typography.fontWeight.bold }}>817-447-4433</strong>
                </div>
              </div>
            </Card>
          </div>
        )}

        <form onSubmit={handlePlaceOrder}>
          <div style={gridStyles}>
            {/* Left Column - Forms */}
            <div>
              {/* Shipping Address */}
              <Card 
                padding="xl" 
                style={{ 
                  marginBottom: theme.spacing.xl,
                  background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
                  border: `1px solid ${theme.colors.borderLight}`,
                  boxShadow: theme.shadows.lg,
                }}
              >
                <h3 style={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontSize: theme.typography.fontSize['2xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  marginBottom: theme.spacing.xl,
                  paddingBottom: theme.spacing.md,
                  borderBottom: `2px solid ${theme.colors.primary}`,
                  color: theme.colors.text,
                }}>
                  Shipping Address
                </h3>

                {/* Saved addresses */}
                {savedAddresses.length > 0 && !useNewAddress && (
                  <div style={{ marginBottom: theme.spacing.lg }}>
                    {savedAddresses.map((addr) => (
                      <div
                        key={addr.id}
                        onClick={() => handleAddressSelect(addr.id)}
                        style={radioCardStyles(selectedAddressId === addr.id)}
                      >
                        <div style={{ display: 'flex', alignItems: 'start', gap: theme.spacing.sm }}>
                          <input
                            type="radio"
                            name="address"
                            checked={selectedAddressId === addr.id}
                            onChange={() => handleAddressSelect(addr.id)}
                            style={{ marginTop: '4px' }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: theme.typography.fontWeight.semibold }}>
                              {addr.full_name}
                              {addr.is_default && (
                                <span style={{
                                  marginLeft: theme.spacing.sm,
                                  fontSize: theme.typography.fontSize.xs,
                                  color: theme.colors.primary,
                                  backgroundColor: theme.colors.surfaceAlt,
                                  padding: '2px 8px',
                                  borderRadius: theme.borderRadius.sm,
                                }}>
                                  Default
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                              {addr.address_line1}
                              {addr.address_line2 && `, ${addr.address_line2}`}
                            </div>
                            <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                              {addr.city}, {addr.state} {addr.postal_code}
                            </div>
                            <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                              {addr.country}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      onClick={handleUseNewAddress}
                      style={{ 
                        marginTop: theme.spacing.md,
                        padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                        fontSize: theme.typography.fontSize.base,
                        fontWeight: theme.typography.fontWeight.semibold,
                        fontFamily: theme.typography.fontFamily.secondary,
                        background: 'transparent',
                        color: theme.colors.primary,
                        border: `2px solid ${theme.colors.primary}`,
                        borderRadius: theme.borderRadius.lg,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.colors.primary;
                        e.currentTarget.style.color = '#fff';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = theme.shadows.md;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = theme.colors.primary;
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      + Use a new address
                    </button>
                  </div>
                )}

                {/* New address form */}
                {(savedAddresses.length === 0 || useNewAddress) && (
                  <>
                    {savedAddresses.length > 0 && (
                      <BackButton
                        variant="ghost"
                        size="sm"
                        label="Back to saved addresses"
                        onClick={() => {
                          setUseNewAddress(false);
                          if (savedAddresses.length > 0) {
                            handleAddressSelect(savedAddresses[0].id);
                          }
                        }}
                        style={{ marginBottom: theme.spacing.md }}
                      />
                    )}
                    
                    <div style={{ display: 'grid', gap: theme.spacing.md }}>
                      <div>
                        <label style={labelStyles}>Full Name *</label>
                        <input
                          type="text"
                          required
                          value={shippingAddress.full_name}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, full_name: e.target.value })}
                          style={inputStyles}
                        />
                      </div>

                      <div>
                        <label style={labelStyles}>Address Line 1 *</label>
                        <input
                          type="text"
                          required
                          value={shippingAddress.address_line1}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, address_line1: e.target.value })}
                          style={inputStyles}
                        />
                      </div>

                      <div>
                        <label style={labelStyles}>Address Line 2</label>
                        <input
                          type="text"
                          value={shippingAddress.address_line2}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, address_line2: e.target.value })}
                          style={inputStyles}
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
                        <div>
                          <label style={labelStyles}>City *</label>
                          <input
                            type="text"
                            required
                            value={shippingAddress.city}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                            style={inputStyles}
                          />
                        </div>
                        <div>
                          <label style={labelStyles}>State *</label>
                          <input
                            type="text"
                            required
                            value={shippingAddress.state}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                            style={inputStyles}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
                        <div>
                          <label style={labelStyles}>ZIP Code *</label>
                          <input
                            type="text"
                            required
                            value={shippingAddress.postal_code}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, postal_code: e.target.value })}
                            style={inputStyles}
                          />
                        </div>
                        <div>
                          <label style={labelStyles}>Country *</label>
                          <select
                            required
                            value={shippingAddress.country}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                            style={inputStyles}
                          >
                            <option value="US">United States</option>
                            <option value="CA">Canada</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label style={labelStyles}>Phone</label>
                        <input
                          type="tel"
                          value={shippingAddress.phone}
                          onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                          style={inputStyles}
                        />
                      </div>
                    </div>
                  </>
                )}
              </Card>

              {/* Shipping Method */}
              <Card 
                padding="xl" 
                style={{ 
                  marginBottom: theme.spacing.xl,
                  background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
                  border: `1px solid ${theme.colors.borderLight}`,
                  boxShadow: theme.shadows.lg,
                }}
              >
                <h3 style={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontSize: theme.typography.fontSize['2xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  marginBottom: theme.spacing.xl,
                  paddingBottom: theme.spacing.md,
                  borderBottom: `2px solid ${theme.colors.primary}`,
                  color: theme.colors.text,
                }}>
                  Shipping Method
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                  {/* Standard Shipping Option */}
                  <div
                    onClick={() => setShippingMethod('standard')}
                    style={radioCardStyles(shippingMethod === 'standard')}
                  >
                    <div style={{ display: 'flex', alignItems: 'start', gap: theme.spacing.sm }}>
                      <input
                        type="radio"
                        name="shipping_method"
                        checked={shippingMethod === 'standard'}
                        onChange={() => setShippingMethod('standard')}
                        style={{ marginTop: '4px' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontWeight: theme.typography.fontWeight.bold,
                          fontSize: theme.typography.fontSize.lg,
                          marginBottom: theme.spacing.xs,
                          color: theme.colors.text,
                        }}>
                          Standard Shipping
                        </div>
                        <div style={{ 
                          fontSize: theme.typography.fontSize.sm, 
                          color: theme.colors.textSecondary,
                          lineHeight: theme.typography.lineHeight.relaxed,
                        }}>
                          Shipping method and cost will be calculated and confirmed by our team during order review.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Customer Pickup Option */}
                  <div
                    onClick={() => setShippingMethod('customer_pickup')}
                    style={radioCardStyles(shippingMethod === 'customer_pickup')}
                  >
                    <div style={{ display: 'flex', alignItems: 'start', gap: theme.spacing.sm }}>
                      <input
                        type="radio"
                        name="shipping_method"
                        checked={shippingMethod === 'customer_pickup'}
                        onChange={() => setShippingMethod('customer_pickup')}
                        style={{ marginTop: '4px' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontWeight: theme.typography.fontWeight.bold,
                          fontSize: theme.typography.fontSize.lg,
                          marginBottom: theme.spacing.xs,
                          color: theme.colors.text,
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing.sm,
                        }}>
                          Customer Pickup / Own Freight
                          <span style={{
                            fontSize: theme.typography.fontSize.xs,
                            color: theme.colors.success,
                            backgroundColor: theme.colors.surfaceAlt,
                            padding: '2px 8px',
                            borderRadius: theme.borderRadius.sm,
                            fontWeight: theme.typography.fontWeight.semibold,
                          }}>
                            No Shipping Cost
                          </span>
                        </div>
                        <div style={{ 
                          fontSize: theme.typography.fontSize.sm, 
                          color: theme.colors.textSecondary,
                          lineHeight: theme.typography.lineHeight.relaxed,
                        }}>
                          Pick up your order yourself or arrange your own freight. Shipping cost will be $0.00. You can proceed with immediate payment after order review.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Payment Method Selection (for net30 users) */}
              {isNet30User && (
                <Card 
                  padding="xl"
                  style={{
                    marginBottom: theme.spacing.xl,
                    background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
                    border: `1px solid ${theme.colors.borderLight}`,
                    boxShadow: theme.shadows.lg,
                  }}
                >
                  <h3 style={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: theme.typography.fontSize['2xl'],
                    fontWeight: theme.typography.fontWeight.bold,
                    marginBottom: theme.spacing.xl,
                    paddingBottom: theme.spacing.md,
                    borderBottom: `2px solid ${theme.colors.primary}`,
                    color: theme.colors.text,
                  }}>
                    Payment Method
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                    {/* Card Payment Option */}
                    <div
                      onClick={() => setPaymentMethod('card')}
                      style={radioCardStyles(paymentMethod === 'card')}
                    >
                      <div style={{ display: 'flex', alignItems: 'start', gap: theme.spacing.sm }}>
                        <input
                          type="radio"
                          name="payment_method"
                          checked={paymentMethod === 'card'}
                          onChange={() => setPaymentMethod('card')}
                          style={{ marginTop: '4px' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: theme.typography.fontWeight.bold,
                            fontSize: theme.typography.fontSize.lg,
                            marginBottom: theme.spacing.xs,
                            color: theme.colors.text,
                          }}>
                            {chargesNow ? 'Pay with Card Now' : 'Save Card and Pay Later'}
                          </div>
                          <div style={{ 
                            fontSize: theme.typography.fontSize.sm, 
                            color: theme.colors.textSecondary,
                            lineHeight: theme.typography.lineHeight.relaxed,
                          }}>
                            {chargesNow
                              ? 'Complete payment immediately. Since you\'re picking up, there are no shipping costs and your card will be charged now for the total shown above.'
                              : hasSizeEstimates
                              ? 'Save your card securely. We\'ll charge it once we\'ve measured your pieces and confirmed the final total.'
                              : 'Save your card securely. We\'ll charge it once shipping costs are calculated.'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Invoice Option */}
                    <div
                      onClick={() => setPaymentMethod('invoice')}
                      style={radioCardStyles(paymentMethod === 'invoice')}
                    >
                      <div style={{ display: 'flex', alignItems: 'start', gap: theme.spacing.sm }}>
                        <input
                          type="radio"
                          name="payment_method"
                          checked={paymentMethod === 'invoice'}
                          onChange={() => setPaymentMethod('invoice')}
                          style={{ marginTop: '4px' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: theme.typography.fontWeight.bold,
                            fontSize: theme.typography.fontSize.lg,
                            marginBottom: theme.spacing.xs,
                            color: theme.colors.text,
                          }}>
                            Invoice Me
                          </div>
                          <div style={{ 
                            fontSize: theme.typography.fontSize.sm, 
                            color: theme.colors.textSecondary,
                            lineHeight: theme.typography.lineHeight.relaxed,
                          }}>
                            You will receive an invoice with your appropriate terms.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Card Input Section (for card payments only) */}
              {!(isNet30User && paymentMethod === 'invoice') && (
                <Card 
                  padding="xl"
                  style={{
                    marginBottom: theme.spacing.xl,
                    background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
                    border: `1px solid ${theme.colors.borderLight}`,
                    boxShadow: theme.shadows.lg,
                  }}
                >
                  <h3 style={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: theme.typography.fontSize['2xl'],
                    fontWeight: theme.typography.fontWeight.bold,
                    marginBottom: theme.spacing.xl,
                    paddingBottom: theme.spacing.md,
                    borderBottom: `2px solid ${theme.colors.primary}`,
                    color: theme.colors.text,
                  }}>
                    {shippingMethod === 'customer_pickup' && !(isNet30User && paymentMethod === 'invoice') 
                      ? 'Payment Information' 
                      : 'Payment Information'}
                  </h3>

                  <div style={{ marginBottom: theme.spacing.lg }}>
                    <label style={labelStyles}>Card Details *</label>
                    <div style={{
                      ...inputStyles,
                      padding: theme.spacing.md,
                    }}>
                      <CardElement 
                        options={{
                          style: {
                            base: {
                              fontSize: '16px',
                              color: theme.colors.text,
                              fontFamily: theme.typography.fontFamily.secondary,
                              '::placeholder': {
                                color: theme.colors.textSecondary,
                              },
                            },
                            invalid: {
                              color: theme.colors.error,
                            },
                          },
                        }}
                        onChange={(event) => {
                          setCardComplete(event.complete);
                          setCardError(event.error ? event.error.message : '');
                        }}
                      />
                    </div>
                    {cardError && (
                      <p style={{
                        color: theme.colors.error,
                        fontSize: theme.typography.fontSize.sm,
                        marginTop: theme.spacing.xs,
                        marginBottom: 0,
                      }}>
                        {cardError}
                      </p>
                    )}
                  </div>

                  {/* Consent Checkbox */}
                  <div style={{
                    padding: theme.spacing.lg,
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius.lg,
                    border: `2px solid ${paymentConsent ? theme.colors.primary : theme.colors.borderLight}`,
                    boxShadow: theme.shadows.sm,
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'start',
                      gap: theme.spacing.md,
                      cursor: 'pointer',
                    }}>
                      <input
                        type="checkbox"
                        checked={paymentConsent}
                        onChange={(e) => setPaymentConsent(e.target.checked)}
                        required
                        style={{
                          marginTop: '4px',
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                        }}
                      />
                      <div style={{
                        flex: 1,
                        fontSize: theme.typography.fontSize.sm,
                        color: theme.colors.text,
                        lineHeight: theme.typography.lineHeight.relaxed,
                      }}>
                        {chargesNow && !(isNet30User && paymentMethod === 'invoice') ? (
                          <>
                            <strong>I authorize this company to charge my card for the order total shown above.</strong>
                            <div style={{
                              marginTop: theme.spacing.xs,
                              fontSize: theme.typography.fontSize.xs,
                              color: theme.colors.textSecondary,
                            }}>
                              Your payment will be processed immediately. Since you've selected customer pickup, there are no additional shipping costs to calculate.
                            </div>
                          </>
                        ) : (
                          <>
                            <strong>I authorize this company to securely save my card and charge it for the final order total{isPickup ? '' : ' (including shipping)'} once it is calculated by the admin.</strong>
                            <div style={{
                              marginTop: theme.spacing.xs,
                              fontSize: theme.typography.fontSize.xs,
                              color: theme.colors.textSecondary,
                            }}>
                              {isPickup
                                ? 'Your card will be saved securely with Stripe. You will not be charged until the admin measures your pieces and confirms the final total.'
                                : 'Your card will be saved securely with Stripe. You will not be charged until the admin reviews your order and calculates the final shipping cost.'}
                            </div>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                </Card>
              )}

              {/* Customer Notes */}
              <Card
                padding="xl"
                style={{
                  marginBottom: theme.spacing.xl,
                  background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
                  border: `1px solid ${theme.colors.borderLight}`,
                  boxShadow: theme.shadows.lg,
                }}
              >
                <h3 style={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontSize: theme.typography.fontSize['2xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  marginBottom: theme.spacing.xl,
                  paddingBottom: theme.spacing.md,
                  borderBottom: `2px solid ${theme.colors.primary}`,
                  color: theme.colors.text,
                }}>
                  Order Notes
                </h3>
                <div>
                  <label style={labelStyles}>Add any special instructions or notes for your order (optional)</label>
                  <textarea
                    value={customerNotes}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                    maxLength={500}
                    rows={3}
                    placeholder="e.g. special packaging requests, delivery instructions, etc."
                    style={{
                      ...inputStyles,
                      resize: 'vertical',
                      minHeight: '80px',
                    }}
                  />
                  <div style={{
                    textAlign: 'right',
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.colors.textSecondary,
                    marginTop: theme.spacing.xs,
                  }}>
                    {customerNotes.length}/500
                  </div>
                </div>
              </Card>

              {/* Order Review Note */}
              <Card 
                padding="xl"
                style={{
                  background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
                  border: `1px solid ${theme.colors.borderLight}`,
                  boxShadow: theme.shadows.lg,
                }}
              >
                <h3 style={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontSize: theme.typography.fontSize['2xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  marginBottom: theme.spacing.xl,
                  paddingBottom: theme.spacing.md,
                  borderBottom: `2px solid ${theme.colors.primary}`,
                  color: theme.colors.text,
                }}>
                  How Payment Works
                </h3>

                <div style={{
                  padding: theme.spacing.lg,
                  backgroundColor: theme.colors.surface,
                  borderRadius: theme.borderRadius.lg,
                  border: `2px solid ${theme.colors.borderLight}`,
                  boxShadow: theme.shadows.sm,
                }}>
                  <p style={{ 
                    fontSize: theme.typography.fontSize.base,
                    color: theme.colors.text,
                    marginBottom: theme.spacing.md,
                    fontWeight: theme.typography.fontWeight.semibold,
                  }}>
                    After submitting your order:
                  </p>
                  <ol style={{
                    paddingLeft: theme.spacing.xl,
                    fontSize: theme.typography.fontSize.base,
                    color: theme.colors.text,
                    lineHeight: theme.typography.lineHeight.relaxed,
                    margin: 0,
                  }}>
                    {isNet30User && paymentMethod === 'invoice' ? (
                      <>
                        <li style={{ marginBottom: theme.spacing.sm }}>Our team will review your order</li>
                        <li style={{ marginBottom: theme.spacing.sm }}>
                          {hasSizeEstimates
                            ? 'We\'ll measure the pieces that vary in size and adjust their price to the actual size'
                            : `We'll adjust the final price if needed based on ${isPickup ? 'current inventory' : 'shipping and current inventory'}`}
                        </li>
                        <li>You'll receive an invoice via email with net 30 payment terms</li>
                      </>
                    ) : chargesNow ? (
                      <>
                        <li style={{ marginBottom: theme.spacing.sm }}>Your card will be charged immediately for the amount shown</li>
                        <li style={{ marginBottom: theme.spacing.sm }}>You'll receive a payment confirmation email right away</li>
                        <li style={{ marginBottom: theme.spacing.sm }}>Our team will process your order and prepare it for pickup</li>
                        <li>You'll be notified when your order is ready for pickup</li>
                      </>
                    ) : (
                      <>
                        <li style={{ marginBottom: theme.spacing.sm }}>Your card will be saved securely (no charge yet)</li>
                        {!isPickup && (
                          <li style={{ marginBottom: theme.spacing.sm }}>Our team will calculate the exact shipping cost</li>
                        )}
                        <li style={{ marginBottom: theme.spacing.sm }}>
                          {hasSizeEstimates
                            ? 'We\'ll measure the pieces that vary in size and adjust their price to the actual size'
                            : 'We\'ll adjust the final price if needed based on current inventory'}
                        </li>
                        <li style={{ marginBottom: theme.spacing.sm }}>Your saved card will be charged automatically for the final total</li>
                        <li>You'll receive a confirmation email once payment is complete</li>
                      </>
                    )}
                  </ol>
                  {!(isNet30User && paymentMethod === 'invoice') && (
                    <div style={{
                      marginTop: theme.spacing.lg,
                      padding: theme.spacing.md,
                      backgroundColor: theme.colors.backgroundAlt,
                      borderRadius: theme.borderRadius.md,
                      borderLeft: `3px solid ${theme.colors.primary}`,
                    }}>
                      <p style={{
                        fontSize: theme.typography.fontSize.sm,
                        color: theme.colors.text,
                        margin: 0,
                        lineHeight: theme.typography.lineHeight.relaxed,
                      }}>
                        <strong>💳 Secure Payment:</strong> {chargesNow && !(isNet30User && paymentMethod === 'invoice')
                          ? 'Your payment will be processed immediately using Stripe\'s secure payment system. Since there are no shipping costs to calculate, your card will be charged right away for the total shown.'
                          : `Your card is saved using Stripe's secure payment system. You won't be charged until the admin confirms the final amount${isPickup ? '' : ' including shipping costs'}.`}
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Right Column - Order Summary */}
            <div>
              <Card 
                padding="xl" 
                style={{ 
                  position: 'sticky', 
                  top: '100px',
                  background: `linear-gradient(to bottom, ${theme.colors.surface} 0%, ${theme.colors.surfaceAlt} 100%)`,
                  border: `1px solid ${theme.colors.borderLight}`,
                  boxShadow: theme.shadows.xl,
                }}
              >
                <h3 style={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontSize: theme.typography.fontSize['2xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  marginBottom: theme.spacing.xl,
                  paddingBottom: theme.spacing.md,
                  borderBottom: `2px solid ${theme.colors.primary}`,
                  color: theme.colors.text,
                }}>
                  Order Summary
                </h3>

                {/* Cart Items */}
                <div style={{ marginBottom: theme.spacing.xl }}>
                  {cartItems.map((item) => {
                    const itemKey = cartItemKey(item);
                    return (
                      <div 
                        key={itemKey}
                        style={{
                          marginBottom: theme.spacing.md,
                          padding: theme.spacing.md,
                          backgroundColor: theme.colors.surface,
                          borderRadius: theme.borderRadius.lg,
                          border: `1px solid ${theme.colors.borderLight}`,
                          boxShadow: theme.shadows.sm,
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'start',
                          gap: theme.spacing.md,
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: theme.typography.fontWeight.semibold,
                              fontSize: theme.typography.fontSize.base,
                              color: theme.colors.text,
                              marginBottom: theme.spacing.xs,
                            }}>
                              {item.name}
                            </div>
                            {(item.variantName || item.subcategory_name) && (
                              <div style={{ 
                                color: theme.colors.primary,
                                fontWeight: theme.typography.fontWeight.semibold,
                                fontSize: theme.typography.fontSize.sm,
                                marginBottom: theme.spacing.xs,
                                padding: '4px 8px',
                                backgroundColor: `${theme.colors.primary}10`,
                                borderRadius: theme.borderRadius.sm,
                                display: 'inline-block',
                              }}>
                                {item.variantName || item.subcategory_name}
                              </div>
                            )}
                            {item.selected_components && item.selected_components.length > 0 && (
                              <div style={{
                                fontSize: theme.typography.fontSize.xs,
                                color: theme.colors.textSecondary,
                                marginBottom: theme.spacing.xs,
                                marginTop: theme.spacing.xs,
                                padding: theme.spacing.xs,
                                backgroundColor: theme.colors.backgroundAlt,
                                borderRadius: theme.borderRadius.sm,
                                borderLeft: `2px solid ${theme.colors.accent}`,
                              }}>
                                <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: '2px' }}>
                                  + Customizations:
                                </div>
                                {item.selected_components.map((comp, idx) => (
                                  <div key={idx} style={{ paddingLeft: theme.spacing.xs, fontSize: theme.typography.fontSize.xs }}>
                                    • {comp.name}{comp.variation_name ? ` (${comp.variation_name})` : ''}
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Price with discount display */}
                            {(() => {
                              const currentPrice = Number(item.price) || 0;
                              const basePrice = Number(item.base_price_without_discount) || currentPrice;
                              const hasDiscount = Math.abs(currentPrice - basePrice) > 0.01;
                              
                              return (
                                <div style={{ marginTop: theme.spacing.xs, marginBottom: theme.spacing.xs }}>
                                  {hasDiscount ? (
                                    <>
                                      <div style={{ marginBottom: '4px' }}>
                                        <span style={{
                                          fontSize: theme.typography.fontSize.xs,
                                          color: theme.colors.textSecondary,
                                        }}>
                                          Original: 
                                        </span>
                                        <span style={{
                                          fontSize: theme.typography.fontSize.sm,
                                          color: theme.colors.textSecondary,
                                          textDecoration: 'line-through',
                                          marginLeft: '4px',
                                        }}>
                                          ${basePrice.toFixed(2)}
                                        </span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{
                                          fontSize: theme.typography.fontSize.sm,
                                          fontWeight: theme.typography.fontWeight.semibold,
                                          color: theme.colors.primary,
                                        }}>
                                          ${currentPrice.toFixed(2)} each
                                        </span>
                                        <div style={{
                                          display: 'inline-block',
                                          padding: '2px 6px',
                                          backgroundColor: '#dcfce7',
                                          border: '1px solid #86efac',
                                          borderRadius: theme.borderRadius.sm,
                                          fontSize: '0.65rem',
                                          color: '#16a34a',
                                          fontWeight: 600,
                                        }}>
                                          {((basePrice - currentPrice) / basePrice * 100).toFixed(0)}% off
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <div style={{
                                      fontSize: theme.typography.fontSize.sm,
                                      color: theme.colors.textSecondary,
                                    }}>
                                      ${currentPrice.toFixed(2)} each
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {approxSqftOf(item) && (
                              <div style={{
                                fontSize: theme.typography.fontSize.xs,
                                color: '#92400E',
                                fontWeight: theme.typography.fontWeight.semibold,
                                marginTop: theme.spacing.xs,
                              }}>
                                Estimated price · about {formatSqft(approxSqftOf(item))} sq ft each
                              </div>
                            )}
                            <div style={{
                              fontSize: theme.typography.fontSize.sm,
                              color: theme.colors.textSecondary,
                              marginTop: theme.spacing.xs,
                            }}>
                              Quantity: {item.quantity}
                            </div>
                          </div>
                          <div style={{
                            fontWeight: theme.typography.fontWeight.bold,
                            fontSize: theme.typography.fontSize.lg,
                            color: theme.colors.primary,
                            whiteSpace: 'nowrap',
                          }}>
                            ${((Number(item.price) || 0) * item.quantity).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Totals */}
                <div style={{ marginBottom: theme.spacing.xl }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: theme.spacing.md,
                    fontSize: theme.typography.fontSize.base,
                    color: theme.colors.text,
                  }}>
                    <span>Subtotal</span>
                    <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: theme.spacing.md,
                    fontSize: theme.typography.fontSize.base,
                    color: theme.colors.text,
                  }}>
                    <span>Shipping</span>
                    {shippingMethod === 'customer_pickup' ? (
                      <span style={{ 
                        fontWeight: theme.typography.fontWeight.semibold,
                        color: theme.colors.success,
                      }}>
                        $0.00
                      </span>
                    ) : (
                      <span style={{ 
                        fontStyle: 'italic', 
                        color: theme.colors.textSecondary,
                        fontWeight: theme.typography.fontWeight.medium,
                      }}>
                        TBD
                      </span>
                    )}
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: theme.spacing.lg,
                    fontSize: theme.typography.fontSize.base,
                    color: theme.colors.text,
                  }}>
                    <span>{isWholesaleOrVolume && shippingMethod === 'standard' ? 'Tax' : 'Tax (Est.)'}</span>
                    {isWholesaleOrVolume && shippingMethod === 'standard' ? (
                      <span style={{ 
                        fontStyle: 'italic', 
                        color: theme.colors.textSecondary,
                        fontWeight: theme.typography.fontWeight.medium,
                      }}>
                        TBD
                      </span>
                    ) : (
                      <span style={{ fontWeight: theme.typography.fontWeight.semibold }}>
                        ${tax.toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.colors.textSecondary,
                    fontStyle: 'italic',
                    marginTop: `-${theme.spacing.sm}`,
                    marginBottom: theme.spacing.lg,
                  }}>
                    Tax will be removed by an admin if you qualify as tax exempt
                  </div>
                  <div style={{
                    borderTop: `2px solid ${theme.colors.border}`,
                    paddingTop: theme.spacing.lg,
                    marginTop: theme.spacing.lg,
                    backgroundColor: theme.colors.surface,
                    padding: theme.spacing.lg,
                    borderRadius: theme.borderRadius.lg,
                    marginLeft: `-${theme.spacing.lg}`,
                    marginRight: `-${theme.spacing.lg}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <span style={{
                      fontFamily: theme.typography.fontFamily.primary,
                      fontSize: theme.typography.fontSize['2xl'],
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.colors.text,
                    }}>
                      {hasSizeEstimates ? 'Estimated Total' : 'Total'}
                    </span>
                    <span style={{ 
                      fontFamily: theme.typography.fontFamily.primary,
                      color: theme.colors.primary,
                      fontSize: theme.typography.fontSize['2xl'],
                      fontWeight: theme.typography.fontWeight.bold,
                    }}>
                      ${(isWholesaleOrVolume && shippingMethod === 'standard' ? subtotal : total).toFixed(2)}
                    </span>
                  </div>
                </div>

                {hasSizeEstimates && (
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: theme.spacing.md,
                    padding: theme.spacing.md,
                    marginBottom: theme.spacing.lg,
                    backgroundColor: '#FFFBEB',
                    border: `2px solid ${sizeEstimateAccepted ? theme.colors.primary : '#FCD34D'}`,
                    borderRadius: theme.borderRadius.lg,
                    cursor: 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={sizeEstimateAccepted}
                      onChange={(e) => setSizeEstimateAccepted(e.target.checked)}
                      style={{ marginTop: '3px', width: '18px', height: '18px', flexShrink: 0, cursor: 'pointer' }}
                    />
                    <span style={{
                      fontSize: theme.typography.fontSize.sm,
                      color: theme.colors.text,
                      lineHeight: theme.typography.lineHeight.relaxed,
                    }}>
                      <strong>I understand some items vary in size.</strong> Their prices are estimates for an average piece. My final price will be based on the actual size of the pieces I receive, and may be higher or lower than shown.
                    </span>
                  </label>
                )}

                {(() => {
                  const isInvoice = isNet30User && paymentMethod === 'invoice';
                  const isButtonDisabled = loading || hasUnavailableItems || (hasSizeEstimates && !sizeEstimateAccepted) || !shippingAddress.city || !shippingAddress.postal_code || (!isInvoice && (!paymentConsent || !cardComplete));
                  return (
                <button
                  type="submit"
                  disabled={isButtonDisabled}
                  style={{
                    width: '100%',
                    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                    fontSize: theme.typography.fontSize.lg,
                    fontWeight: theme.typography.fontWeight.bold,
                    fontFamily: theme.typography.fontFamily.secondary,
                    background: isButtonDisabled
                      ? theme.colors.borderLight
                      : `linear-gradient(135deg, ${theme.colors.cta} 0%, ${theme.colors.primaryLight} 100%)`,
                    color: '#fff',
                    border: 'none',
                    borderRadius: theme.borderRadius.lg,
                    boxShadow: isButtonDisabled
                      ? 'none'
                      : '0 4px 12px rgba(204, 85, 0, 0.3)',
                    cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: theme.spacing.sm,
                    opacity: isButtonDisabled ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!isButtonDisabled) {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(204, 85, 0, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isButtonDisabled) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(204, 85, 0, 0.3)';
                    }
                  }}
                >
                  {loading ? (
                    <>
                      <span style={{
                        display: 'inline-block',
                        width: '20px',
                        height: '20px',
                        border: '3px solid rgba(255,255,255,0.3)',
                        borderTop: '3px solid #fff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }} />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      {isNet30User && paymentMethod === 'invoice' ? (
                        <span>Submit Order for Review</span>
                      ) : chargesNow ? (
                        <span>Pay Now & Submit Order</span>
                      ) : (
                        <span>Save Card & Submit Order</span>
                      )}
                    </>
                  )}
                </button>
                  );
                })()}

                <p style={{
                  marginTop: theme.spacing.lg,
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.textSecondary,
                  textAlign: 'center',
                  lineHeight: theme.typography.lineHeight.relaxed,
                }}>
                  By placing your order, you agree to our terms and conditions.
                </p>
                
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </Card>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        @media (max-width: 768px) {
          div[style*="grid-template-columns: 2fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
        }
        
        input[type="text"]:focus,
        input[type="tel"]:focus,
        select:focus {
          border-color: ${theme.colors.primary} !important;
          box-shadow: 0 0 0 3px rgba(204, 85, 0, 0.1) !important;
          outline: none;
        }
        
        input[type="text"]:hover,
        input[type="tel"]:hover,
        select:hover {
          border-color: ${theme.colors.primaryLight};
        }
      `}</style>
    </div>
  );
};

export default CheckoutPage;
